import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { IllustrationFileType } from '../DocumentIllustration';

export interface FileTypePillProps {
  /** Which file type this pill represents. Drives the dot color and label. */
  fileType: IllustrationFileType;
  /** Visual treatment. Defaults to `banner`. `banner` = translucent backdrop blur (for overlay on banner image). `inline` = solid surface (for inline use, e.g. list rows). */
  variant?: 'banner' | 'inline';
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

// PowerPoint files are keyed as `ppt` (matching IllustrationFileType) but
// display as `PPTX` since that's the more familiar modern extension.
const LABELS: Record<IllustrationFileType, string> = {
  pdf: 'PDF',
  doc: 'DOC',
  html: 'HTML',
  ppt: 'PPTX',
  audio: 'AUDIO',
  img: 'IMAGE',
  video: 'VIDEO',
  view: 'VIEW',
  archive: 'ARCHIVE',
};

/**
 * Translucent pill showing a file-type label with a brand-colored leading dot.
 * Used as an overlay on tile banners and inline next to titles in compact contexts.
 *
 * No auth, no router, no business logic.
 */
export const FileTypePill: React.FC<FileTypePillProps> = ({ fileType, variant = 'banner', sx }) => (
  <Box
    sx={[
      (theme) => {
        const dot = theme.palette.fileDots[fileType];
        const isBanner = variant === 'banner';
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '999px',
          background: isBanner
            ? (theme.palette.mode === 'light' ? 'rgba(255,255,255,.94)' : 'rgba(15,30,51,.92)')
            : theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          backdropFilter: isBanner ? 'blur(6px)' : 'none',
          boxShadow: isBanner ? '0 1px 2px rgba(15,23,42,.06)' : 'none',
          border: isBanner ? 'none' : `1px solid ${theme.palette.divider}`,
          '&::before': {
            content: '""',
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: dot,
          },
        };
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {LABELS[fileType]}
  </Box>
);

export default FileTypePill;
